'use client';

import type { ReactNode, MouseEventHandler } from 'react';

/**
 * DashedAddButton — the bordered-dashed "create/commit" action used for
 * adding new items (another colorway, new link, etc.).
 *
 * Style: paper-2 fill, 1px dashed ink border, mono-caps, generous padding.
 * This is the canonical pattern for secondary "add/create" actions that
 * live below inputs or inside cards/modals.
 *
 * Extracted during Phase 1 foundation work (build 1426).
 */

interface DashedAddButtonProps {
  onClick: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  /** 'bar' = full-width create bar, 'round' = small 24x24 round add button (sits in swatch grids, matches the round colorway swatches next to it). */
  size?: 'bar' | 'round';
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
  'data-testid'?: string;
}

export function DashedAddButton({
  onClick,
  children,
  size = 'bar',
  className,
  style,
  'aria-label': ariaLabel,
  'data-testid': dataTestid,
}: DashedAddButtonProps) {
  const baseStyle: React.CSSProperties =
    size === 'round'
      ? {
          width: 24,
          height: 24,
          background: 'var(--paper-2)',
          border: '1px dashed var(--ink)',
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--ink)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
        }
      : {
          width: '100%',
          background: 'var(--paper-2)',
          border: '1px dashed var(--ink)',
          padding: '12px 0',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--ink)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          borderRadius: 0,
        };

  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      style={{ ...baseStyle, ...style }}
      aria-label={ariaLabel}
      data-testid={dataTestid}
    >
      {children}
    </button>
  );
}

export default DashedAddButton;